import React, { useEffect } from 'react';
import { Box, Flex, Text, Badge, Heading, SimpleGrid, Tooltip, VStack, Divider, HStack } from '@chakra-ui/react';
import { TtScore, Competitor } from '../../types/types';
import { debugTtScores } from '../../utils/debug';
import { formatTimeWithSecondsInTz, tzShortLabel } from '../../utils/timezone';
import { useTimezone } from '../../contexts/TimezoneContext';

/**
 * Format timestamp to show both UTC and the selected display timezone (time only).
 * @param timestamp Unix timestamp in seconds
 * @param timeZone IANA zone ('' = browser local)
 */
const formatTimestamp = (timestamp: number, timeZone: string): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000);
  const utcTime = date.toISOString().substring(11, 19);
  const zoneTime = formatTimeWithSecondsInTz(date, timeZone);
  const zoneLabel = timeZone ? tzShortLabel(timeZone) : 'Local';
  return `UTC: ${utcTime}\n${zoneLabel}: ${zoneTime}`;
};

interface TtScoreSequenceProps {
  ttScores: TtScore[];
  firstCompetitor: Competitor;
  secondCompetitor: Competitor;
}

/**
 * Displays the played points of a table tennis match grouped by set, colouring each point by the
 * competitor who won it. Only point-winning snapshots are shown; non-point snapshots (e.g. set
 * summaries) are skipped so the point count matches the set score.
 */
export const TtScoreSequence: React.FC<TtScoreSequenceProps> = ({
  ttScores,
  firstCompetitor,
  secondCompetitor
}) => {
  const [timeZone] = useTimezone();
  useEffect(() => {
    debugTtScores(ttScores, 'TtScoreSequence');
  }, [ttScores]);

  // If no scores are available, don't render anything
  if (!ttScores || ttScores.length === 0) {
    return null;
  }

  // Keep only snapshots that record a won point by one of the two competitors
  const points = ttScores.filter(s =>
    s.pointWinnerCompetitorId === firstCompetitor.id || s.pointWinnerCompetitorId === secondCompetitor.id);

  if (points.length === 0) {
    return null;
  }

  // Group points by set, chronologically within each set
  const bySet: Record<number, TtScore[]> = {};
  points.forEach(score => {
    (bySet[score.currentSet] = bySet[score.currentSet] || []).push(score);
  });
  const sets = Object.keys(bySet).map(Number).sort((a, b) => a - b);
  sets.forEach(set => bySet[set].sort((a, b) => a.sequenceId - b.sequenceId));

  // Final set score is the highest points each competitor reached in that set (robust to ordering)
  const setPointsFor = (setScores: TtScore[], competitorId: number) =>
    setScores.reduce((max, s) => {
      const p = s.players?.find(pl => pl.competitorId === competitorId)?.currentSetPoints ?? 0;
      return p > max ? p : max;
    }, 0);

  return (
    <Box>
      <Heading size="xs" mb={1.5}>Points by Set</Heading>

      {/* Legend: which number/colour is which player */}
      <HStack spacing={3} mb={2}>
        <HStack spacing={1}>
          <Box w={4} h={4} borderRadius="sm" bg="blue.100" borderWidth="1px" display="flex" alignItems="center" justifyContent="center">
            <Text fontWeight="bold" fontSize="xs">1</Text>
          </Box>
          <Text fontSize="xs">{firstCompetitor.name}</Text>
        </HStack>
        <HStack spacing={1}>
          <Box w={4} h={4} borderRadius="sm" bg="green.100" borderWidth="1px" display="flex" alignItems="center" justifyContent="center">
            <Text fontWeight="bold" fontSize="xs">2</Text>
          </Box>
          <Text fontSize="xs">{secondCompetitor.name}</Text>
        </HStack>
      </HStack>

      {sets.map((set, index) => {
        const setScores = bySet[set];
        const firstPts = setPointsFor(setScores, firstCompetitor.id);
        const secondPts = setPointsFor(setScores, secondCompetitor.id);

        return (
          <React.Fragment key={set}>
            {index > 0 && <Divider my={1} />}
            <VStack align="start" spacing={1} mb={2}>
              <Flex align="center" width="100%">
                <Badge colorScheme="blue" mr={2}>Set {set}</Badge>
                <Text fontSize="sm" fontWeight="medium">
                  {setScores.length} points ({firstPts}–{secondPts})
                </Text>
              </Flex>
              <SimpleGrid columns={{ base: 6, md: 12 }} spacing={1} width="100%" maxH="150px" overflowY="auto">
                {setScores.map(score => {
                  const wonByFirst = score.pointWinnerCompetitorId === firstCompetitor.id;
                  const winner = wonByFirst ? firstCompetitor : secondCompetitor;
                  return (
                    <Tooltip
                      key={score.sequenceId}
                      label={`Point won by ${winner.name}\n${formatTimestamp(score.timestamp, timeZone)}`}
                    >
                      <Box
                        p={1}
                        borderWidth="1px"
                        borderRadius="sm"
                        bg={wonByFirst ? 'blue.100' : 'green.100'}
                        textAlign="center"
                      >
                        <Text fontWeight="bold" fontSize="sm">{wonByFirst ? 1 : 2}</Text>
                      </Box>
                    </Tooltip>
                  );
                })}
              </SimpleGrid>
            </VStack>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default TtScoreSequence;
